import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Image as ImageIcon, Video, Paperclip, X, Loader2, Download, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import {
  useJournalEntryAttachments,
  useUploadJournalAttachment,
  useDetachJournalEntryDocument,
  validateJournalAttachmentFile,
  type JournalAttachmentRow,
} from '@/hooks/useJournalAttachments';
import { useGetDocumentDownloadUrl } from '@/hooks/useDocuments';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

interface JournalAttachmentsProps {
  clinicId: string;
  patientId: string;
  entryId?: string | null;
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
  canEdit?: boolean;
}

export function JournalAttachments({
  clinicId,
  patientId,
  entryId,
  pendingFiles = [],
  onPendingFilesChange,
  canEdit = false,
}: JournalAttachmentsProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const attachmentsQuery = useJournalEntryAttachments(entryId ?? null);
  const uploadAttachment = useUploadJournalAttachment(clinicId);
  const detachAttachment = useDetachJournalEntryDocument(clinicId);
  const getDownloadUrl = useGetDocumentDownloadUrl();
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);

  const isPendingMode = !entryId && onPendingFilesChange;
  const showFilePicker = canEdit || isPendingMode;

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files?.length) return;

    for (const file of Array.from(files)) {
      const validationError = validateJournalAttachmentFile(file);
      if (validationError) {
        toast.error(t(`journal.attachments.errors.${validationError}`));
        continue;
      }

      if (isPendingMode) {
        onPendingFilesChange([...pendingFiles, file]);
        continue;
      }

      if (!entryId) continue;

      try {
        await uploadAttachment.mutateAsync({
          clinicId,
          patientId,
          journalEntryId: entryId,
          file,
        });
        toast.success(t('journal.attachments.uploaded'));
      } catch {
        toast.error(t('journal.attachments.errors.upload_failed'));
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  };

  const handleView = (source: FilePreviewSource) => {
    setPreview(source);
  };

  const handleDownload = async (documentId: string) => {
    try {
      const url = await getDownloadUrl.mutateAsync(documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('journal.attachments.errors.download_failed'));
    }
  };

  const handleRemovePending = (index: number) => {
    if (!onPendingFilesChange) return;
    onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
  };

  const handleDetach = async (attachment: JournalAttachmentRow) => {
    if (!entryId) return;
    try {
      await detachAttachment.mutateAsync({
        attachment_id: attachment.id,
        journal_entry_id: entryId,
      });
      toast.success(t('journal.attachments.removed'));
    } catch {
      toast.error(t('journal.attachments.errors.remove_failed'));
    }
  };

  const attachments = attachmentsQuery.data ?? [];
  const isBusy = uploadAttachment.isPending || detachAttachment.isPending || getDownloadUrl.isPending;

  return (
    <div>
      {preview && (
        <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} />
      )}
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {t('journal.attachments.title')}
        </label>
        {showFilePicker && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,.avi,application/pdf,image/*,video/*"
              className="hidden"
              onChange={(e) => void handleFilesSelected(e.target.files)}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isBusy}
              onClick={() => inputRef.current?.click()}
            >
              {uploadAttachment.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
              {t('journal.attachments.add')}
            </Button>
          </>
        )}
      </div>

      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        {t('journal.attachments.hint')}
      </p>

      {entryId && attachmentsQuery.isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <ul className="space-y-2">
          {attachments.map((attachment) => {
            const Icon = attachmentIcon(attachment.document.mime_type);
            return (
              <li
                key={attachment.id}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60"
              >
                <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() =>
                      handleView({
                        type: 'document',
                        documentId: attachment.document_id,
                        filename: attachment.document.original_filename,
                        mimeType: attachment.document.mime_type,
                      })
                    }
                    className="block w-full truncate text-left text-sm text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
                  >
                    {attachment.document.original_filename}
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(attachment.document.size_bytes)}
                  </p>
                </div>
                <button
                  type="button"
                  title={t('journal.attachments.view')}
                  onClick={() =>
                    handleView({
                      type: 'document',
                      documentId: attachment.document_id,
                      filename: attachment.document.original_filename,
                      mimeType: attachment.document.mime_type,
                    })
                  }
                  className="rounded p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title={t('journal.attachments.download')}
                  onClick={() => void handleDownload(attachment.document_id)}
                  className="rounded p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <Download className="h-4 w-4" />
                </button>
                {canEdit && entryId && (
                  <button
                    type="button"
                    title={t('journal.attachments.remove')}
                    onClick={() => void handleDetach(attachment)}
                    className="rounded p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}

          {pendingFiles.map((file, index) => {
            const mime = file.type || 'application/octet-stream';
            const Icon = attachmentIcon(mime);
            return (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
              >
                <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handleView({ type: 'local', file })}
                    className="block w-full truncate text-left text-sm text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
                  >
                    {file.name}
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)} · {t('journal.attachments.pending')}
                  </p>
                </div>
                {isPendingMode && (
                  <button
                    type="button"
                    title={t('journal.attachments.view')}
                    onClick={() => handleView({ type: 'local', file })}
                    className="rounded p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                )}
                {isPendingMode && (
                  <button
                    type="button"
                    title={t('journal.attachments.remove')}
                    onClick={() => handleRemovePending(index)}
                    className="rounded p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}

          {!attachments.length && !pendingFiles.length && (
            <li className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-400 dark:border-gray-700 dark:text-gray-500">
              {t('journal.attachments.empty')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
